import { Course, Match, PlayerResult, TeamGameMatch, TeamGameMatchTeam, TeamGamePlayerDisplay } from '../../database.schema';
import { BACK_NINE_INDEX, FRONT_NINE_INDEX } from '../app/constants';

type Values = {
  handicap: number;
  strokeIndex: number;
  par: number;
  score: number;
};

export const calculateShots = (handicap: number, strokeIndex: number): number => {
  switch (true) {
    case handicap < strokeIndex: {
      return 0;
    }
    case handicap > 18 && handicap - 18 >= strokeIndex: {
      return 2;
    }
    default:
      return 1;
  }
};

export const calculateScore = (values: Values): number => {
  const { handicap, strokeIndex, par, score } = values;
  const shots = calculateShots(handicap, strokeIndex);

  const adjustedPar = par + shots;

  switch (true) {
    // 1 over
    case adjustedPar - score === -1: {
      return 1;
    }
    // par
    case adjustedPar === score: {
      return 2;
    }
    // one under
    case adjustedPar - score === 1: {
      return 3;
    }
    // two under
    case adjustedPar - score === 2: {
      return 4;
    }
    // three under
    case adjustedPar - score === 3: {
      return 5;
    }
    // four under
    case adjustedPar - score === 4: {
      return 6;
    }
    default:
      return 0;
  }
};

export const isMatchFinished = (resultsByHole: Array<string>, holeIndex: number) => {
  const slicedArray = resultsByHole.slice(0, holeIndex);
  const team1HolesWon = slicedArray.filter((result: string) => result === 't1').length;
  const team2HolesWon = slicedArray.filter((result: string) => result === 't2').length;
  if (team1HolesWon > team2HolesWon && team1HolesWon - team2HolesWon > 18 - holeIndex) {
    return true;
  }
  if (team2HolesWon > team1HolesWon && team2HolesWon - team1HolesWon > 18 - holeIndex) {
    return true;
  }
  return false;
};

export const calculateOveralMatchResult = (resultsByHole: Array<string>): string => {
  const team1HolesWon = resultsByHole.filter((result: string) => result === 't1').length;
  const team2HolesWon = resultsByHole.filter((result: string) => result === 't2').length;
  if (isMatchFinished(resultsByHole, resultsByHole.length)) {
    const nonResultHoles = resultsByHole.filter((result: string) => result === '-').length;
    const winHolesLeft = nonResultHoles + 18 - resultsByHole.length;
    if (team1HolesWon > team2HolesWon) {
      return `t1 ${team1HolesWon - team2HolesWon}${winHolesLeft === 0 ? ' up' : ` & ${winHolesLeft}`}`;
    }
    if (team2HolesWon > team1HolesWon) {
      return `t2 ${team2HolesWon - team1HolesWon}${winHolesLeft === 0 ? ' up' : ` & ${winHolesLeft}`}`;
    }
  }
  if (team1HolesWon > team2HolesWon) {
    return `t1 ${team1HolesWon - team2HolesWon}up`;
  }
  if (team1HolesWon < team2HolesWon) {
    return `t2 ${team2HolesWon - team1HolesWon}up`;
  }
  return '-- A/S';
};
export const getTeamPoints = (team: string, resultsByHole: Array<string>): number => {
  const matchResult = calculateOveralMatchResult(resultsByHole);
  if (matchResult === '-- A/S') {
    return 0.5;
  }
  if (team === 'team1') {
    if (matchResult.startsWith('t1')) {
      return 1;
    }
  }
  if (team === 'team2') {
    if (matchResult.startsWith('t2')) {
      return 1;
    }
  }
  return 0;
};

export const getLowestHandicap = (recordedMatch: Match): number => {
  return [...recordedMatch.team1.players, ...recordedMatch.team2.players].map((player: PlayerResult) => player.round.individualHandicap).sort((a, b) => a - b)[0];
};

export const getLowestNetScoreUsingMatchShots = (players: Array<PlayerResult>, holeIndex: number, holeStrokeIndex: number): number => {
  return [...players]
    .map((player: PlayerResult) => {
      const playerScore = player.round.scoreCard[holeIndex];
      return holeStrokeIndex <= player.round.shotsReceived ? playerScore - calculateShots(player.round.shotsReceived, holeStrokeIndex) : playerScore;
    })
    .sort()[0];
};

export const getResultDisplayColorTextForOverAllResult = (result: string) => {
  if (result.startsWith('t1')) {
    return 'red';
  }
  if (result.startsWith('t2')) {
    return 'blue';
  }
  return 'gray';
};

export const getPlayerScore = (handicap: number, scoreCard: Array<number>, course: Course, startingHole: number) => {
  const holeIndexes = startingHole === 1 ? [...FRONT_NINE_INDEX, ...BACK_NINE_INDEX] : [...BACK_NINE_INDEX, ...FRONT_NINE_INDEX];
  return scoreCard.reduce(
    (acc, hole, index) =>
      acc +
      calculateScore({
        par: course.scoreCard[holeIndexes[index]],
        score: hole,
        handicap,
        strokeIndex: course.strokeIndex[holeIndexes[index]],
      }),
    0,
  );
};
